import Vue from 'vue'
import Vuex from 'vuex'
import Buefy, {ConfigProgrammatic} from 'buefy'
import store from './store'
import Form from './utils/Form'
import router from './utils/Router'
import i18n from './utils/i18n'
import moment from 'moment-timezone'
import VueScrollTo from 'vue-scrollto'
import posthog from 'posthog-js'
import App from './views/App.vue'
import VueCurrencyFilter from 'vue-currency-filter'
import * as Sentry from '@sentry/vue'
import VueSimpleMarkdown from 'vue-simple-markdown'
import './utils/ComponentRegister'
import './components/Popup'
import './filters/filters'
import localDateUtil from "./utils/DateTime";
import VerificationManager from "./utils/VerificationManager";

window.axios = require('axios')
window.moment = moment
window.Form = Form
window.moment.locale(localStorage.getItem('locale') || 'nl')

if (process.env.NODE_ENV === 'production') {
    const sentryScrubUrl = (url) => {
        if (typeof url !== 'string') return url
        return url
            .replace(/\/giftcards\/(.+?)(\/(?:redeem|issue|extend|transactions)\b)/, '/giftcards/[Filtered]$2')
            .replace(/\/giftcards\/search\/.+/, '/giftcards/search/[Filtered]')
            .replace(/\/giftcards\/(?!search|\[Filtered])[^?]+/, '/giftcards/[Filtered]')
            .replace(/\/balance\/[^?/]+/, '/balance/[Filtered]')
            .replace(/\/wallet\/download\/[^?/]+/, '/wallet/download/[Filtered]')
            .replace(/\/wallet\/(?!download)[^?/]+\/[^?/]+/, '/wallet/[Filtered]/[Filtered]')
            .replace(/\/passes\/[^/]+\/[^?/]+/, (match) => {
                const parts = match.split('/')
                return parts.slice(0, -1).join('/') + '/[Filtered]'
            })
            .replace(/\/registrations\/[^/]+\/[^?/]+/, (match) => {
                const parts = match.split('/')
                return parts.slice(0, -1).join('/') + '/[Filtered]'
            })
    }

    const sentrySensitiveKeys = [
        'code', 'secret', 'coupon', 'action_code', 'account_iban', 'account_bic',
        'card_number', 'card_cvc', 'card_expiry', 'password', 'password_confirmation',
        'current_password', 'token', 'giftcard',
    ]

    const sentryScrubData = (data) => {
        if (typeof data !== 'object' || data === null) return data
        const scrubbed = Array.isArray(data) ? [...data] : {...data}
        for (const key in scrubbed) {
            if (typeof scrubbed[key] === 'object' && scrubbed[key] !== null) {
                scrubbed[key] = sentryScrubData(scrubbed[key])
            } else if (sentrySensitiveKeys.includes(key.toLowerCase())) {
                scrubbed[key] = '[Filtered]'
            }
        }
        return scrubbed
    }

    Sentry.init({
        Vue,
        dsn: 'https://57e90ca2bf704efb9a5d60d3dc55e67d@o109994.ingest.sentry.io/4504537158057984',
        release: process.env.MIX_BUILD_NUMBER,
        environment: process.env.APP_ENV,
        tracePropagationTargets: [
            "localhost", // For local development
            /^\/api\//, // For same-origin API calls
            process.env.APP_URL,
            "https://api.gifty.nl",
            "https://dashboard.gifty.nl",
        ],
        integrations: [
            Sentry.browserTracingIntegration({router}),
            Sentry.replayIntegration({
                maskAllInputs: true,
                blockAllMedia: true,
            }),
        ],
        beforeSend(event) {
            if (event.request?.url) {
                event.request.url = sentryScrubUrl(event.request.url)
            }
            if (event.transaction) {
                event.transaction = sentryScrubUrl(event.transaction)
            }
            if (event.request?.data && typeof event.request.data === 'object') {
                event.request.data = sentryScrubData(event.request.data)
            }
            if (event.request?.cookies) {
                delete event.request.cookies
            }
            if (event.request?.headers) {
                delete event.request.headers['Authorization']
                delete event.request.headers['Cookie']
            }
            return event
        },
        beforeBreadcrumb(breadcrumb) {
            if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
                if (breadcrumb.data?.url) {
                    breadcrumb.data.url = sentryScrubUrl(breadcrumb.data.url)
                }
            }
            if (breadcrumb.category === 'navigation') {
                if (breadcrumb.data?.from) {
                    breadcrumb.data.from = sentryScrubUrl(breadcrumb.data.from)
                }
                if (breadcrumb.data?.to) {
                    breadcrumb.data.to = sentryScrubUrl(breadcrumb.data.to)
                }
            }
            return breadcrumb
        },
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
        tracesSampleRate: 0.3,
        tracingOptions: {
            trackComponents: true,
        },
    })

    posthog.init('phc_kMPiu1ZUB5We1WLu9X0YOCo8FEzQtiwGik9CAMXw8G7', {
        api_host: 'https://eu.posthog.com',
        property_blacklist: [
            'password',
            'password_confirmation',
            'card_number',
            'card_cvc',
            'card_expiry',
            'giftcard',
            'gift_card',
            'giftCard',
            'code'
        ],
    })
}

Vue.use(Buefy)
Vue.use(VueCurrencyFilter, {
    symbol: '€',
    thousandsSeparator: '.',
    fractionCount: 2,
    fractionSeparator: ',',
    symbolPosition: 'front',
    symbolSpacing: true
})
Vue.use(Vuex)
Vue.use(VueScrollTo)
Vue.use(VueSimpleMarkdown)
Vue.prototype.$i18nDate = localDateUtil

new Vue({
    el: '#gifty',
    template: '<App/>',
    components: {
        App: App,
    },
    store,
    router,
    i18n,
    mounted() {
        window.axios.interceptors.request.use((config) => {
            // For each request, we set common headers
            config.headers['X-Requested-With'] = 'XMLHttpRequest'
            config.headers['X-Client-Application'] = `Gifty Dashboard ${process.env.MIX_BUILD_NUMBER}`
            config.headers['Accept-Language'] = this.$i18n.locale

            // Append X-Gifty-Account header if the activeCompanyId key is set
            if (localStorage.hasOwnProperty('activeCompanyId')) {
                config.headers['X-Gifty-Account'] = localStorage.getItem('activeCompanyId')
            }

            return config
        })

        window.axios.interceptors.response.use((response) => {
            return response
        }, async (error) => {
            if (typeof error.response === 'undefined') {
                console.info('Interceptor: no response data, so rejecting promise')

                return Promise.reject(error)
            }

            // Most likely expected form validation error
            if (error.response.status === 422) {
                console.info('Interceptor: seems like form error, so rejecting promise')

                return Promise.reject(error)
            }

            // Session expired. Logout endpoint is excluded to prevent loops.
            if (error.response.status === 401 && (error.request?.responseURL || '').indexOf('/auth/logout') === -1) {
                console.info('Interceptor: expired session')

                // Show the API error message when on the login page (e.g. no active company)
                if (router.currentRoute.name === 'auth.login' && error.response.data && error.response.data.message) {
                    this.$buefy.toast.open({
                        message: error.response.data.message,
                        type: 'is-danger'
                    })
                }

                store.dispatch('user/logout')
                    .finally(() => {
                        if (router.currentRoute.name !== 'auth.login') {
                            router.push({name: 'auth.login'})
                        }
                    })
            }

            // Missing required permissions
            if (error.response.status === 403) {
                console.info('Interceptor: wrong permissions, redirecting to home')

                // Redirect to home, ignoring NavigationDuplicated
                router.push({name: 'home'}).catch(navigationError => {
                    if (navigationError.name !== 'NavigationDuplicated') {
                        console.error('Navigation error:', navigationError)
                    }
                });

                // Return original error
                return Promise.reject(error)
            }

            // CSRF token mismatch, reload page
            if (error.response.status === 419) {
                console.info('Interceptor: CSRF expired, reloading page')

                window.location.reload()

                return new Promise(() => {})
            }

            if (
                // (2FA) Verification required
                error.response.status === 428
            ) {
                console.info('Verification required by server')

                try {
                    return await VerificationManager.handleVerificationRequired(error)
                } catch (verificationError) {
                    // When the user cancels verification, reject with the original
                    // axios error so downstream handlers can safely access .response
                    error.verificationCancelled = true
                    return Promise.reject(error)
                }
            }

            return Promise.reject(error)
        })

        ConfigProgrammatic.setOptions({
            defaultDialogConfirmText: this.$t('confirmation_dialog.confirm'),
            defaultDialogCancelText: this.$t('confirmation_dialog.deny'),
            defaultFirstDayOfWeek: moment.localeData().firstDayOfWeek(),
            defaultDayNames: moment.localeData().weekdaysShort(),
            defaultMonthNames: moment.localeData().months(),
            defaultDateFormatter: date => moment(date).format('DD-MM-YYYY')
        })
    },
})
